import React from 'react';
import { useQuery } from '@apollo/client';
import { ArtistSchema } from '../../../types/schema';
import { GetPlaylistsDocument } from '../../../graphql/generated-types/graphql';
import { Loading } from '../../loading/loading';
import { style } from 'typestyle';
import { spacing } from '../../../theme/spacing';
import { PlaylistTile } from '../molecules/PlaylistTile';
import FavouritePlaylistButtonCompat from '../../compat/favourite-playlist-button-compat';
import {
  mediaDesktop,
  mediaLargeMobileOnly,
  mediaLargeTabletOnly,
  mediaSmallTabletOnly,
} from '../utilities/mediaQueries';

interface ArtistPlaylistsProps {
  artist: ArtistSchema;
}

const styles = {
  container: style(),
  grid: style(
    {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: spacing.DEFAULT,
    },
    mediaLargeMobileOnly({
      gridTemplateColumns: 'repeat(3, 1fr)',
    }),
    mediaSmallTabletOnly({
      gridTemplateColumns: 'repeat(4, 1fr)',
    }),
    mediaLargeTabletOnly({
      gridTemplateColumns: 'repeat(4, 1fr)',
    })
  ),
  playlistTile: style({
    paddingBottom: 0,
  }),
};

export const ArtistPlaylists: React.FC<ArtistPlaylistsProps> = ({ artist }) => {
  const { loading, error, data } = useQuery(GetPlaylistsDocument, {
    variables: {
      filters: {
        artist: artist.identity,
      },
    },
  });

  if (loading) return <Loading text="Loading playlists" className={styles.container} />;

  if (error) return null;

  const playlists = data.playlists.matches.results;

  if (!playlists.length) return null;

  const playlistTiles = playlists.map((playlist) => {
    // The playlist tile requires images in a different format to what we receive from graphql
    const formattedPlaylist = {
      ...playlist,
      images: (playlist.images && playlist.images[0]) || { identity: undefined },
      use_playlist_image: true,
    };

    return (
      <PlaylistTile
        key={formattedPlaylist.identity}
        playlist={formattedPlaylist}
        className={styles.playlistTile}
        usePlaceholderImage={true}
        size="large"
      >
        <FavouritePlaylistButtonCompat identity={formattedPlaylist.identity} />
      </PlaylistTile>
    );
  });

  return (
    <div className={styles.container}>
      <h3>Artist features in</h3>

      <div className={styles.grid}>{playlistTiles}</div>
    </div>
  );
};
